import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { getUiLang } from '../../utils/lang';
import { 
  Plus, Edit, Trash2, Search, X, Save, Loader2, 
  ChevronRight, ChevronDown, Folder, FolderOpen, Package, Filter
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import ImageUploader from '../../components/ImageUploader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CrossSellPicker = ({ products, lang, selectedIds, excludeId, onChange }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const getName = (p) =>
    p?.translations?.[lang]?.name ||
    p?.translations?.sk?.name ||
    p?.translations?.en?.name ||
    Object.values(p?.translations || {})[0]?.name ||
    p?.sku || '';

  const selected = selectedIds
    .map((id) => products.find((p) => p.id === id))
    .filter(Boolean);

  const q = query.trim().toLowerCase();
  const candidates = q.length >= 2
    ? products.filter((p) => {
        if (p.id === excludeId) return false;
        if (selectedIds.includes(p.id)) return false;
        const name = getName(p).toLowerCase();
        return name.includes(q) || (p.sku || '').toLowerCase().includes(q);
      }).slice(0, 8)
    : [];

  const add = (id) => {
    onChange([...selectedIds, id]);
    setQuery('');
  };
  const remove = (id) => onChange(selectedIds.filter((x) => x !== id));
  const move = (idx, dir) => {
    const next = [...selectedIds];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  return (
    <div className="border-t pt-4" data-testid="admin-cross-sell-picker">
      <h3 className="font-semibold mb-1">
        {t('admin.cross_sell.title', 'Často kupované spolu')}
      </h3>
      <p className="text-xs text-gray-500 mb-3">
        {t('admin.cross_sell.help', 'Vyberte produkty, ktoré sa zobrazia na detaile tohto produktu ako odporúčania. Max 8.')}
      </p>

      {selected.length > 0 ? (
        <ul className="space-y-1.5 mb-3" data-testid="cross-sell-selected-list">
          {selected.map((p, idx) => (
            <li key={p.id} className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded px-2.5 py-1.5 text-sm">
              <span className="flex-1 truncate">
                <span className="font-mono text-xs text-gray-500 mr-2">#{idx + 1}</span>
                {getName(p)} <span className="text-gray-400 text-xs">({p.sku})</span>
              </span>
              <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0}
                className="px-1.5 text-gray-500 hover:text-orange-600 disabled:opacity-30" title={t('admin.products_page.move_up', 'Up')}>↑</button>
              <button type="button" onClick={() => move(idx, 1)} disabled={idx === selected.length - 1}
                className="px-1.5 text-gray-500 hover:text-orange-600 disabled:opacity-30" title={t('admin.products_page.move_down', 'Down')}>↓</button>
              <button type="button" onClick={() => remove(p.id)}
                className="px-1.5 text-red-500 hover:text-red-700" data-testid={`cross-sell-remove-${p.sku}`}>
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-gray-400 italic mb-3">{t('admin.cross_sell.empty', 'Žiadne odporúčania.')}</p>
      )}

      <div className="relative" data-testid="cross-sell-search-wrap">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('admin.cross_sell.search', 'Vyhľadať produkt podľa názvu alebo SKU...')}
          className="pl-9"
          disabled={selectedIds.length >= 8}
          data-testid="cross-sell-search-input"
        />
        {candidates.length > 0 && (
          <ul className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-64 overflow-auto">
            {candidates.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => add(p.id)}
                  className="w-full text-left px-3 py-2 hover:bg-orange-50 text-sm flex items-center justify-between gap-2"
                  data-testid={`cross-sell-add-${p.sku}`}
                >
                  <span className="truncate">{getName(p)}</span>
                  <span className="text-xs text-gray-400 font-mono flex-shrink-0">{p.sku}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="text-xs text-gray-400 mt-1">
        {selectedIds.length}/8
      </p>
    </div>
  );
};

const emptyProduct = {
  sku: '',
  brand: '',
  brand_id: '',
  category_id: '',
  price_eur: 0,
  price_usd: 0,
  stock: 0,
  unit: 'mb',
  bulk_threshold_m: 30,
  bulk_discount_percent: 20,
  min_order_step_m: 5,
  min_stock_alert: null,
  roll_width_m: null,
  images: [''],
  is_featured: false,
  cross_sell_ids: [],
  translations: {
    sk: { name: '', description: '' },
    en: { name: '', description: '' },
    de: { name: '', description: '' },
    cs: { name: '', description: '' },
    ru: { name: '', description: '' }
  },
  specifications: {}
};

const AdminProducts = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkCrossSellIds, setBulkCrossSellIds] = useState([]);
  const [bulkMode, setBulkMode] = useState('replace');
  const [bulkSaving, setBulkSaving] = useState(false);
  // Bulk Stock Adjustment modal state
  const [bulkStockOpen, setBulkStockOpen] = useState(false);
  const [stockAdjMode, setStockAdjMode] = useState('set');
  const [stockAdjAmount, setStockAdjAmount] = useState('');
  const [stockAdjReason, setStockAdjReason] = useState('');
  const [stockAdjReasonCustom, setStockAdjReasonCustom] = useState('');
  const [stockAdjNote, setStockAdjNote] = useState('');
  const [stockAdjSaving, setStockAdjSaving] = useState(false);

  const languages = ['sk', 'en', 'de', 'cs', 'ru'];
  const languageNames = { sk: 'SK', en: 'EN', de: 'DE', cs: 'CS', ru: 'RU' };
  const [activeLang, setActiveLang] = useState('sk');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, brandsRes, categoriesRes] = await Promise.all([
        fetch(`${API_URL}/api/products`),
        fetch(`${API_URL}/api/brands`),
        fetch(`${API_URL}/api/categories`)
      ]);
      
      if (productsRes.ok) setProducts(await productsRes.json());
      if (brandsRes.ok) setBrands(await brandsRes.json());
      if (categoriesRes.ok) {
        const cats = await categoriesRes.json();
        setCategories(cats);
        // Auto-expand root categories
        const expanded = {};
        cats.filter(c => !c.parent_id).forEach(c => expanded[c.id] = true);
        setExpandedCategories(expanded);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('admin.products_page.fetch_error', 'Chyba pri načítaní dát'));
    } finally {
      setLoading(false);
    }
  };

  const getTranslation = (item, field) => {
    return item?.translations?.[getUiLang(i18n)]?.[field] || 
           item?.translations?.['sk']?.[field] || 
           item?.translations?.['en']?.[field] || '';
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? getTranslation(cat, 'name') : '-';
  };

  const getCategoryPath = (categoryId) => {
    const parts = [];
    let current = categories.find(c => c.id === categoryId);
    while (current) {
      parts.unshift(getTranslation(current, 'name'));
      current = categories.find(c => c.id === current.parent_id);
    }
    return parts.join(' → ');
  };

  const getChildren = (parentId) => {
    return categories.filter(c => c.parent_id === parentId);
  };

  const toggleExpand = (catId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [catId]: !prev[catId]
    }));
  };

  const handleDelete = async (productId) => {
    if (!window.confirm(t('admin.confirm_delete'))) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/products/${productId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setProducts(products.filter(p => p.id !== productId));
        toast.success(t('admin.product_deleted'));
      } else {
        toast.error(t('common.error'));
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error(t('common.error'));
    }
  };

  const handleSave = async (product) => {
    setSaving(true);
    console.log('Saving product:', JSON.stringify(product, null, 2));
    try {
      const url = isCreating 
        ? `${API_URL}/api/admin/products`
        : `${API_URL}/api/admin/products/${product.id}`;
      
      console.log('Request URL:', url, 'Method:', isCreating ? 'POST' : 'PUT');
      
      const response = await fetch(url, {
        method: isCreating ? 'POST' : 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(product)
      });
      
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('Success result:', result);
        fetchData();
        setEditingProduct(null);
        setIsCreating(false);
        toast.success(isCreating ? t('admin.product_created') : t('admin.product_updated'));
      } else {
        const err = await response.json();
        console.error('Error response:', err);
        toast.error(err.detail || t('common.error'));
      }
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = (categoryId = '') => {
    setIsCreating(true);
    setEditingProduct({ ...emptyProduct, category_id: categoryId });
  };

  // Filter products by search and category
  const filteredProducts = products.filter(p => {
    const name = p.translations?.sk?.name || p.name || '';
    const matchesSearch = name.toLowerCase().includes(search.toLowerCase()) ||
                          p.sku?.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !selectedCategoryFilter || p.category_id === selectedCategoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Group products by category for display
  const getProductsByCategory = (categoryId) => {
    return filteredProducts.filter(p => p.category_id === categoryId);
  };

  const getProductsWithoutCategory = () => {
    return filteredProducts.filter(p => !p.category_id);
  };

  // Render category tree for filter sidebar
  const renderCategoryFilter = (parentId = null, level = 0) => {
    const items = categories.filter(c => {
      if (parentId === null) {
        return c.parent_id === null || c.parent_id === undefined || c.parent_id === '';
      }
      return c.parent_id === parentId;
    });
    
    return items.map(cat => {
      const children = getChildren(cat.id);
      const hasChildren = children.length > 0;
      const isExpanded = expandedCategories[cat.id];
      const productCount = getProductsByCategory(cat.id).length;
      const isSelected = selectedCategoryFilter === cat.id;

      return (
        <div key={cat.id}>
          <div 
            className={`flex items-center gap-1 py-1.5 px-2 rounded cursor-pointer transition ${
              isSelected ? 'bg-orange-100 text-orange-600' : 'hover:bg-gray-100'
            }`}
            style={{ paddingLeft: `${level * 16 + 8}px` }}
          >
            {hasChildren ? (
              <button onClick={() => toggleExpand(cat.id)} className="p-0.5">
                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            ) : (
              <span className="w-5" />
            )}
            <span 
              className="flex-1 text-sm truncate"
              onClick={() => setSelectedCategoryFilter(isSelected ? '' : cat.id)}
            >
              {getTranslation(cat, 'name')}
            </span>
            <span className="text-xs text-gray-400">{productCount}</span>
            <button
              onClick={() => handleCreate(cat.id)}
              className="p-1 rounded hover:bg-green-100 text-green-600 opacity-0 group-hover:opacity-100"
              title={t('admin.products_page.add_product', 'Pridať produkt')}
            >
              <Plus className="h-3 w-3" />
            </button>
          </div>
          {hasChildren && isExpanded && renderCategoryFilter(cat.id, level + 1)}
        </div>
      );
    });
  };

  // Render category picker for product form
  const renderCategoryPicker = (parentId = null, level = 0) => {
    const items = categories.filter(c => {
      // Handle both null and undefined parent_id values for root categories
      if (parentId === null) {
        return c.parent_id === null || c.parent_id === undefined || c.parent_id === '';
      }
      return c.parent_id === parentId;
    });
    
    return items.map(cat => {
      const children = getChildren(cat.id);
      const hasChildren = children.length > 0;
      const isSelected = editingProduct?.category_id === cat.id;

      return (
        <div key={cat.id}>
          <div 
            className={`flex items-center gap-2 py-2 px-3 rounded cursor-pointer transition ${
              isSelected ? 'bg-orange-100 text-orange-600 font-medium' : 'hover:bg-gray-50'
            }`}
            style={{ paddingLeft: `${level * 20 + 12}px` }}
            onClick={() => {
              setEditingProduct(prev => ({ ...prev, category_id: cat.id }));
              setShowCategoryPicker(false);
            }}
          >
            {hasChildren ? (
              <Folder className="h-4 w-4 text-orange-400" />
            ) : (
              <Package className="h-4 w-4 text-gray-400" />
            )}
            <span className="text-sm">{getTranslation(cat, 'name')}</span>
          </div>
          {hasChildren && renderCategoryPicker(cat.id, level + 1)}
        </div>
      );
    });
  };

  const updateProductField = (field, value) => {
    setEditingProduct(prev => ({ ...prev, [field]: value }));
  };

  const updateTranslation = (lang, field, value) => {
    setEditingProduct(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: {
          ...prev.translations[lang],
          [field]: value
        }
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">{t('admin.products')}</h1>
        <Button 
          className="bg-orange-500 hover:bg-orange-600"
          onClick={() => handleCreate()}
          data-testid="add-product-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('admin.add_product')}
        </Button>
      </div>

      <div className="flex gap-6">
        {/* Left Sidebar - Category Filter */}
        <div className="w-64 flex-shrink-0">
          <div className="bg-white rounded-xl shadow p-4 sticky top-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Filter className="h-4 w-4" />
              {t('admin.categories_menu')}
            </h3>
            
            <div 
              className={`flex items-center gap-2 py-1.5 px-2 rounded cursor-pointer transition mb-2 ${
                !selectedCategoryFilter ? 'bg-orange-100 text-orange-600' : 'hover:bg-gray-100'
              }`}
              onClick={() => setSelectedCategoryFilter('')}
            >
              <Package className="h-4 w-4" />
              <span className="text-sm flex-1">{t('catalog.all_categories')}</span>
              <span className="text-xs text-gray-400">{products.length}</span>
            </div>

            <div className="border-t pt-2 mt-2">
              {renderCategoryFilter()}
            </div>

            {getProductsWithoutCategory().length > 0 && (
              <div 
                className={`flex items-center gap-2 py-1.5 px-2 rounded cursor-pointer transition mt-2 border-t pt-2 ${
                  selectedCategoryFilter === 'none' ? 'bg-orange-100 text-orange-600' : 'hover:bg-gray-100'
                }`}
                onClick={() => setSelectedCategoryFilter('none')}
              >
                <Package className="h-4 w-4 text-gray-400" />
                <span className="text-sm flex-1 text-gray-500">{t('admin.products_page.no_category', 'Bez kategórie')}</span>
                <span className="text-xs text-gray-400">{getProductsWithoutCategory().length}</span>
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {/* Search */}
          <div className="bg-white rounded-xl shadow p-4 mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                placeholder={t('admin.search_products')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="product-search"
              />
            </div>
          </div>

          {/* Bulk-actions toolbar (visible when ≥1 selected) */}
          {selectedRowIds.size > 0 && (
            <div className="mb-3 flex flex-wrap items-center gap-3 bg-orange-50 border border-orange-200 rounded-lg px-4 py-2" data-testid="bulk-toolbar">
              <span className="text-sm font-medium text-orange-900" data-testid="bulk-selected-count">
                {t('admin.bulk_selected', '{{count}} vybraných', { count: selectedRowIds.size })}
              </span>
              <Button
                size="sm"
                onClick={() => { setBulkCrossSellIds([]); setBulkMode('replace'); setBulkModalOpen(true); }}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="bulk-set-crosssell-btn"
              >
                <Package className="h-4 w-4 mr-1.5" />
                {t('admin.bulk_set_crosssell', 'Nastaviť cross-sell')}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setStockAdjMode('set');
                  setStockAdjAmount('');
                  setStockAdjReason('');
                  setStockAdjReasonCustom('');
                  setStockAdjNote('');
                  setBulkStockOpen(true);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="bulk-stock-adjust-btn"
              >
                <Package className="h-4 w-4 mr-1.5" />
                {t('admin.stock_adjust.button', 'Korekcia skladu')}
              </Button>
              <button
                type="button"
                onClick={() => setSelectedRowIds(new Set())}
                className="text-sm text-gray-600 hover:text-gray-900 underline"
                data-testid="bulk-clear-btn"
              >
                {t('admin.bulk_clear', 'Zrušiť výber')}
              </button>
            </div>
          )}

          {/* Products Table */}
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={filteredProducts.length > 0 && filteredProducts.every((p) => selectedRowIds.has(p.id))}
                        onChange={(e) => {
                          const next = new Set(selectedRowIds);
                          if (e.target.checked) filteredProducts.forEach((p) => next.add(p.id));
                          else filteredProducts.forEach((p) => next.delete(p.id));
                          setSelectedRowIds(next);
                        }}
                        className="rounded"
                        data-testid="bulk-select-all"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.product_name')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.categories_menu')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.brand')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.price_eur')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.stock')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredProducts.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                        {search || selectedCategoryFilter
                    ? t('admin.products_page.empty_filter', 'Žiadne produkty nevyhovujú filtru')
                    : t('admin.products_page.empty_none', 'Zatiaľ nie sú žiadne produkty')}
                      </td>
                    </tr>
                  ) : (
                    filteredProducts.map(product => (
                      <tr key={product.id} className="hover:bg-gray-50" data-testid={`product-row-${product.id}`}>
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            checked={selectedRowIds.has(product.id)}
                            onChange={(e) => {
                              const next = new Set(selectedRowIds);
                              if (e.target.checked) next.add(product.id);
                              else next.delete(product.id);
                              setSelectedRowIds(next);
                            }}
                            className="rounded"
                            data-testid={`bulk-select-${product.id}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <img
                              src={product.images?.[0] || 'https://via.placeholder.com/40'}
                              alt=""
                              className="w-10 h-10 rounded object-cover mr-3"
                            />
                            <div>
                              <div className="font-medium text-sm">{getTranslation(product, 'name') || product.sku}</div>
                              <div className="text-xs text-gray-500">{product.sku}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                            {getCategoryName(product.category_id)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">{product.brand || '-'}</td>
                        <td className="px-4 py-3 font-medium text-sm">
                          <div>€{product.price_eur?.toFixed(2)}<span className="text-xs text-gray-400">/{product.unit || 'mb'}</span></div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            product.stock > 30 ? 'bg-green-100 text-green-800' :
                            product.stock > 0 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {product.stock} {product.unit || 'mb'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => { setEditingProduct(product); setIsCreating(false); }}
                              className="p-1.5 rounded hover:bg-blue-100 text-blue-600"
                              data-testid={`edit-product-${product.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(product.id)}
                              className="p-1.5 rounded hover:bg-red-100 text-red-600"
                              data-testid={`delete-product-${product.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Edit/Create Modal */}
      {editingProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-bold">
                {isCreating ? t('admin.add_product') : t('admin.edit_product')}
              </h2>
              <button onClick={() => { setEditingProduct(null); setIsCreating(false); }}>
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">SKU *</label>
                  <Input
                    value={editingProduct.sku}
                    onChange={(e) => updateProductField('sku', e.target.value)}
                    placeholder="ABC-001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('admin.brand')}</label>
                  <select
                    className="w-full border rounded-lg p-2"
                    value={editingProduct.brand_id}
                    onChange={(e) => {
                      const brand = brands.find(b => b.id === e.target.value);
                      updateProductField('brand_id', e.target.value);
                      updateProductField('brand', brand?.name || '');
                    }}
                  >
                    <option value="">-- {t('admin.select_brand')} --</option>
                    {brands.map(b => (
                      <option key={b.id} value={b.id}>{b.name || getTranslation(b, 'name')}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Category Selection */}
              <div className="relative">
                <label className="block text-sm font-medium mb-1">{t('admin.categories_menu')} *</label>
                <div 
                  className="border rounded-lg p-3 cursor-pointer hover:bg-gray-50 transition"
                  onClick={() => setShowCategoryPicker(!showCategoryPicker)}
                >
                  {editingProduct.category_id ? (
                    <span className="text-sm">{getCategoryPath(editingProduct.category_id)}</span>
                  ) : (
                    <span className="text-sm text-gray-400">-- Vyberte kategoriu --</span>
                  )}
                </div>
                {showCategoryPicker && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowCategoryPicker(false)} />
                    <div className="absolute z-50 left-0 right-0 border rounded-lg mt-1 max-h-64 overflow-y-auto bg-white shadow-xl">
                      {renderCategoryPicker()}
                    </div>
                  </>
                )}
              </div>

              {/* Prices & Warehouse */}
              <div>
                <label className="block text-sm font-medium mb-2">Ceny a sklad</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">EUR / {editingProduct.unit || 'mb'}</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingProduct.price_eur || 0}
                      onChange={(e) => updateProductField('price_eur', parseFloat(e.target.value) || 0)}
                      data-testid="price-eur"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">USD / {editingProduct.unit || 'mb'}</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingProduct.price_usd || 0}
                      onChange={(e) => updateProductField('price_usd', parseFloat(e.target.value) || 0)}
                      data-testid="price-usd"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('admin.stock')} *</label>
                    <Input
                      type="number"
                      step="0.5"
                      value={editingProduct.stock}
                      onChange={(e) => updateProductField('stock', parseFloat(e.target.value) || 0)}
                      data-testid="stock-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Jednotka</label>
                    <select
                      className="w-full border rounded-lg p-2 h-10"
                      value={editingProduct.unit || 'mb'}
                      onChange={(e) => updateProductField('unit', e.target.value)}
                      data-testid="unit-select"
                    >
                      <option value="mb">{t('admin.products_page.unit_bm', 'bm (bežný meter / linear meter)')}</option>
                      <option value="pcs">ks (kus)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Krok odrezu</label>
                    <Input
                      type="number"
                      step="1"
                      value={editingProduct.min_order_step_m ?? 5}
                      onChange={(e) => updateProductField('min_order_step_m', parseFloat(e.target.value) || 5)}
                      data-testid="min-order-step"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('admin.products_page.roll_width', 'Šírka rolky (m)')}</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingProduct.roll_width_m ?? ''}
                      onChange={(e) => updateProductField('roll_width_m', e.target.value === '' ? null : parseFloat(e.target.value))}
                      placeholder="1.52"
                      data-testid="roll-width"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('admin.products_page.bulk_threshold', 'Hranica zľavy (mb)')}</label>
                    <Input
                      type="number"
                      step="1"
                      value={editingProduct.bulk_threshold_m ?? 30}
                      onChange={(e) => updateProductField('bulk_threshold_m', parseFloat(e.target.value) || 30)}
                      data-testid="bulk-threshold"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('admin.products_page.bulk_discount', 'Zľava (%)')}</label>
                    <Input
                      type="number"
                      step="1"
                      value={editingProduct.bulk_discount_percent ?? 20}
                      onChange={(e) => updateProductField('bulk_discount_percent', parseFloat(e.target.value) || 0)}
                      data-testid="bulk-discount-pct"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Min. zostatok (alert)</label>
                    <Input
                      type="number"
                      step="1"
                      value={editingProduct.min_stock_alert ?? ''}
                      onChange={(e) => updateProductField('min_stock_alert', e.target.value === '' ? null : parseFloat(e.target.value))}
                      placeholder="auto"
                      data-testid="min-stock-alert"
                    />
                  </div>
                </div>
              </div>

              {/* Image */}
              <div>
                <label className="block text-sm font-medium mb-1">{t('admin.image')}</label>
                <ImageUploader
                  token={token}
                  currentImage={editingProduct.images?.[0] || ''}
                  onImageUploaded={(url) => updateProductField('images', url ? [url] : [])}
                />
              </div>

              {/* Featured */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_featured"
                  checked={editingProduct.is_featured}
                  onChange={(e) => updateProductField('is_featured', e.target.checked)}
                  className="rounded"
                />
                <label htmlFor="is_featured" className="text-sm">{t('admin.featured_product')}</label>
              </div>

              {/* Cross-sell picker — «Часто покупают вместе» */}
              <CrossSellPicker
                products={products}
                lang={getUiLang(i18n)}
                selectedIds={editingProduct.cross_sell_ids || []}
                excludeId={editingProduct.id}
                onChange={(ids) => updateProductField('cross_sell_ids', ids)}
              />

              {/* Translations */}
              <div>
                <h3 className="font-semibold mb-3">{t('admin.translations')}</h3>
                <div className="flex gap-2 mb-4">
                  {languages.map(lang => (
                    <button
                      key={lang}
                      onClick={() => setActiveLang(lang)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                        activeLang === lang 
                          ? 'bg-orange-500 text-white' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {languageNames[lang]}
                    </button>
                  ))}
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('admin.name')} ({languageNames[activeLang]})</label>
                    <Input
                      value={editingProduct.translations?.[activeLang]?.name || ''}
                      onChange={(e) => updateTranslation(activeLang, 'name', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('admin.description')} ({languageNames[activeLang]})</label>
                    <textarea
                      className="w-full border rounded-lg p-3 text-sm"
                      rows={4}
                      value={editingProduct.translations?.[activeLang]?.description || ''}
                      onChange={(e) => updateTranslation(activeLang, 'description', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setEditingProduct(null); setIsCreating(false); }}>
                {t('admin.cancel')}
              </Button>
              <Button 
                onClick={() => handleSave(editingProduct)} 
                disabled={saving}
                className="bg-orange-500 hover:bg-orange-600"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                {t('admin.save')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Cross-sell Modal */}
      {bulkModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="bulk-crosssell-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-xl font-bold">
                  {t('admin.bulk_crosssell_title', 'Hromadné nastavenie cross-sell')}
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {t('admin.bulk_crosssell_subtitle', '{{count}} produktov bude aktualizovaných', { count: selectedRowIds.size })}
                </p>
              </div>
              <button onClick={() => setBulkModalOpen(false)}>
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Mode toggle */}
              <div>
                <label className="block text-sm font-semibold mb-2">{t('admin.bulk_mode', 'Režim')}</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label className={`border-2 rounded-lg p-3 cursor-pointer transition ${bulkMode === 'replace' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input
                      type="radio"
                      name="bulk-mode"
                      value="replace"
                      checked={bulkMode === 'replace'}
                      onChange={() => setBulkMode('replace')}
                      className="mr-2"
                      data-testid="bulk-mode-replace"
                    />
                    <span className="font-medium">{t('admin.bulk_mode_replace', 'Nahradiť')}</span>
                    <p className="text-xs text-gray-500 mt-1">{t('admin.bulk_mode_replace_help', 'Existujúce odporúčania budú prepísané.')}</p>
                  </label>
                  <label className={`border-2 rounded-lg p-3 cursor-pointer transition ${bulkMode === 'append' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input
                      type="radio"
                      name="bulk-mode"
                      value="append"
                      checked={bulkMode === 'append'}
                      onChange={() => setBulkMode('append')}
                      className="mr-2"
                      data-testid="bulk-mode-append"
                    />
                    <span className="font-medium">{t('admin.bulk_mode_append', 'Pridať')}</span>
                    <p className="text-xs text-gray-500 mt-1">{t('admin.bulk_mode_append_help', 'Pridať k existujúcim (max 8, dedup).')}</p>
                  </label>
                </div>
              </div>

              {/* Cross-sell picker (reused) */}
              <CrossSellPicker
                products={products}
                lang={getUiLang(i18n)}
                selectedIds={bulkCrossSellIds}
                excludeId={null}
                onChange={setBulkCrossSellIds}
              />
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBulkModalOpen(false)} disabled={bulkSaving}>
                {t('admin.cancel', 'Zrušiť')}
              </Button>
              <Button
                onClick={async () => {
                  if (bulkCrossSellIds.length === 0) {
                    toast.error(t('admin.bulk_pick_at_least_one', 'Vyberte aspoň jeden produkt'));
                    return;
                  }
                  if (!window.confirm(t('admin.bulk_confirm', 'Naozaj aktualizovať {{count}} produktov?', { count: selectedRowIds.size }))) return;
                  setBulkSaving(true);
                  try {
                    const res = await fetch(`${API_URL}/api/admin/products/bulk/cross-sell`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({
                        product_ids: Array.from(selectedRowIds),
                        cross_sell_ids: bulkCrossSellIds,
                        mode: bulkMode,
                      }),
                    });
                    if (!res.ok) {
                      const err = await res.json().catch(() => ({}));
                      toast.error(err.detail || t('admin.bulk_failed', 'Chyba aktualizácie'));
                      return;
                    }
                    const data = await res.json();
                    toast.success(t('admin.bulk_done', 'Aktualizované: {{u}}, preskočené: {{s}}', { u: data.updated, s: data.skipped }));
                    setBulkModalOpen(false);
                    setSelectedRowIds(new Set());
                    await fetchData();
                  } catch (_e) {
                    toast.error(t('admin.bulk_failed', 'Chyba aktualizácie'));
                  } finally {
                    setBulkSaving(false);
                  }
                }}
                disabled={bulkSaving || bulkCrossSellIds.length === 0}
                className="bg-orange-500 hover:bg-orange-600"
                data-testid="bulk-crosssell-apply"
              >
                {bulkSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                {t('admin.bulk_apply', 'Aplikovať')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Stock Adjustment Modal */}
      {bulkStockOpen && (
        <BulkStockAdjustmentModal
          products={products}
          selectedIds={Array.from(selectedRowIds)}
          mode={stockAdjMode} setMode={setStockAdjMode}
          amount={stockAdjAmount} setAmount={setStockAdjAmount}
          reason={stockAdjReason} setReason={setStockAdjReason}
          reasonCustom={stockAdjReasonCustom} setReasonCustom={setStockAdjReasonCustom}
          note={stockAdjNote} setNote={setStockAdjNote}
          saving={stockAdjSaving}
          onClose={() => setBulkStockOpen(false)}
          onSubmit={async () => {
            const finalReason = stockAdjReason === '__custom' ? stockAdjReasonCustom.trim() : stockAdjReason;
            if (!finalReason || finalReason.length < 3) {
              toast.error(t('admin.stock_adjust.reason_required', 'Príčina je povinná'));
              return;
            }
            const amt = parseFloat(stockAdjAmount);
            if (isNaN(amt) || amt < 0) {
              toast.error(t('admin.stock_adjust.amount_invalid', 'Neplatné množstvo'));
              return;
            }
            setStockAdjSaving(true);
            try {
              const token = localStorage.getItem('sunify_token');
              const res = await fetch(`${API_URL}/api/admin/products/bulk/stock-adjust`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                  product_ids: Array.from(selectedRowIds),
                  mode: stockAdjMode,
                  amount: amt,
                  reason: finalReason,
                  note: stockAdjNote.trim() || undefined,
                }),
              });
              if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                toast.error(err.detail || t('admin.stock_adjust.error', 'Chyba korekcie'));
                return;
              }
              const data = await res.json();
              const failedNote = data.failed > 0
                ? ` (${t('admin.stock_adjust.failed_count', 'chýb')}: ${data.failed})`
                : '';
              toast.success(
                t('admin.stock_adjust.done', 'Aktualizované: {{u}}', { u: data.updated }) + failedNote,
              );
              setBulkStockOpen(false);
              setSelectedRowIds(new Set());
              await fetchData();
            } catch (_e) {
              toast.error(t('admin.stock_adjust.error', 'Chyba korekcie'));
            } finally {
              setStockAdjSaving(false);
            }
          }}
        />
      )}
    </div>
  );
};

// ---------- Bulk Stock Adjustment Modal ----------
const BulkStockAdjustmentModal = ({
  products, selectedIds,
  mode, setMode,
  amount, setAmount,
  reason, setReason,
  reasonCustom, setReasonCustom,
  note, setNote,
  saving, onClose, onSubmit,
}) => {
  const { t } = useTranslation();
  const selectedProducts = products.filter(p => selectedIds.includes(p.id));
  const amt = parseFloat(amount);
  const validAmt = !isNaN(amt) && amt >= 0;

  const preview = selectedProducts.slice(0, 5).map(p => {
    const oldStock = Number(p.stock || 0);
    let newStock = oldStock;
    if (validAmt) {
      if (mode === 'set') newStock = amt;
      else if (mode === 'add') newStock = oldStock + amt;
      else if (mode === 'subtract') newStock = oldStock - amt;
    }
    return { sku: p.sku, oldStock, newStock, willFail: newStock < 0 };
  });

  const reasonPresets = [
    { value: 'inventory', label: t('admin.stock_adjust.reason_inventory', 'Inventúra / Ревизия') },
    { value: 'damaged', label: t('admin.stock_adjust.reason_damaged', 'Poškodené / Брак') },
    { value: 'lost', label: t('admin.stock_adjust.reason_lost', 'Strata / Потеря') },
    { value: 'supplier_return', label: t('admin.stock_adjust.reason_supplier_return', 'Vrátenie dodávateľovi') },
    { value: 'transfer', label: t('admin.stock_adjust.reason_transfer', 'Presun na iný sklad') },
    { value: '__custom', label: t('admin.stock_adjust.reason_other', 'Iné (uveďte)') },
  ];

  const finalReasonLen = reason === '__custom' ? reasonCustom.trim().length : reason.length;
  const canSubmit = !saving && validAmt && finalReasonLen >= 3;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="bulk-stock-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">
            {t('admin.stock_adjust.title', 'Korekcia skladu')} · {selectedIds.length}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="bulk-stock-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Mode toggle */}
        <div className="mb-4">
          <div className="text-sm font-medium mb-2">{t('admin.stock_adjust.mode', 'Režim')}</div>
          <div className="flex gap-2">
            {[
              { v: 'set', label: t('admin.stock_adjust.mode_set', 'Nastaviť na hodnotu') },
              { v: 'add', label: t('admin.stock_adjust.mode_add', 'Pripočítať') },
              { v: 'subtract', label: t('admin.stock_adjust.mode_subtract', 'Odpočítať') },
            ].map(({ v, label }) => (
              <button
                key={v}
                type="button"
                onClick={() => setMode(v)}
                className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition ${
                  mode === v
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                }`}
                data-testid={`stock-adj-mode-${v}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Amount */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            {t('admin.stock_adjust.amount', 'Množstvo (mb)')}
          </label>
          <input
            type="number"
            step="0.5"
            min="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="0"
            data-testid="stock-adj-amount"
          />
        </div>

        {/* Reason */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            {t('admin.stock_adjust.reason', 'Príčina')} <span className="text-red-500">*</span>
          </label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg mb-2"
            data-testid="stock-adj-reason"
          >
            <option value="">{t('admin.stock_adjust.reason_pick', '— Vyberte príčinu —')}</option>
            {reasonPresets.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          {reason === '__custom' && (
            <input
              type="text"
              maxLength={200}
              value={reasonCustom}
              onChange={(e) => setReasonCustom(e.target.value)}
              placeholder={t('admin.stock_adjust.reason_custom_ph', 'Vlastná príčina (min 3 znaky)')}
              className="w-full px-3 py-2 border rounded-lg"
              data-testid="stock-adj-reason-custom"
            />
          )}
        </div>

        {/* Note */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            {t('admin.stock_adjust.note', 'Poznámka')} ({t('admin.stock_adjust.optional', 'voliteľné')})
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={500}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder={t('admin.stock_adjust.note_ph', 'napr. č. dodacieho listu, miesto, atď.')}
            data-testid="stock-adj-note"
          />
        </div>

        {/* Preview */}
        {validAmt && preview.length > 0 && (
          <div className="mb-4 bg-gray-50 rounded-lg p-3 text-sm">
            <div className="font-medium mb-2">
              {t('admin.stock_adjust.preview', 'Náhľad ({{n}} z {{total}})', {
                n: preview.length, total: selectedIds.length,
              })}
            </div>
            <div className="space-y-1 font-mono text-xs">
              {preview.map((p, i) => (
                <div key={i} className={p.willFail ? 'text-red-600' : 'text-gray-700'}>
                  {p.sku || '?'}: {p.oldStock} → {p.willFail ? '⚠ ' : ''}{p.newStock.toFixed(2)}
                  {p.willFail && ` (${t('admin.stock_adjust.would_negative', 'záporné — preskočí')})`}
                </div>
              ))}
              {selectedIds.length > preview.length && (
                <div className="text-gray-500">… + {selectedIds.length - preview.length}</div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={onClose} data-testid="stock-adj-cancel">
            {t('common.cancel', 'Zrušiť')}
          </Button>
          <Button
            onClick={onSubmit}
            disabled={!canSubmit}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="stock-adj-submit"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {t('admin.stock_adjust.apply', 'Aplikovať korekciu')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminProducts;
